import { Card } from "@design";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { BrandResponse } from "@/api";
import { getBrandQueryKey, listBrandsQueryKey, updateBrandMutation } from "@/api";
import { EntityFormCard, formText } from "@/components/build/EntityForm";
import { useEntitySave } from "@/components/build/useEntitySave";
import { useAppForm } from "@/components/form/form";
import { BrandKitPreview, type BrandKitValue } from "./BrandKitPreview";

const kitSchema = z.object({
  primary: z.string(),
  secondary: z.string(),
  bg: z.string(),
  text: z.string(),
  accents: z.string(),
  headingFont: z.string(),
  bodyFont: z.string(),
  tone: z.string(),
});
type KitValues = z.infer<typeof kitSchema>;

function kitToForm(brand: BrandResponse): KitValues {
  const kit = brand.brandKit ?? {};
  const palette = (kit.palette ?? {}) as Record<string, unknown>;
  const fonts = (kit.fonts ?? {}) as Record<string, unknown>;
  return {
    primary: (palette.primary as string) ?? "",
    secondary: (palette.secondary as string) ?? "",
    bg: (palette.background as string) ?? "",
    text: (palette.text as string) ?? "",
    accents: Array.isArray(palette.accents) ? palette.accents.join(", ") : "",
    headingFont: (fonts.heading as string) ?? "",
    bodyFont: (fonts.body as string) ?? "",
    tone: (kit.tone as string) ?? "",
  };
}

function formToKit(v: KitValues): Record<string, unknown> {
  return {
    palette: {
      primary: v.primary || undefined,
      secondary: v.secondary || undefined,
      background: v.bg || undefined,
      text: v.text || undefined,
      accents: v.accents
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    },
    fonts: { heading: v.headingFont || undefined, body: v.bodyFont || undefined },
    tone: v.tone || undefined,
  };
}

/**
 * Brand kit tab: the editable palette/fonts/tone form with a live swatch
 * preview. This is the only place the kit is edited (the Overview form holds
 * just identity: name/description/audience).
 */
export function BrandKitTab({ brand }: Readonly<{ brand: BrandResponse }>) {
  const { t } = useTranslation("brands");
  const save = useMutation(updateBrandMutation());
  const { saveExisting } = useEntitySave(formText(t).messages);

  const form = useAppForm({
    defaultValues: kitToForm(brand),
    validators: { onChange: kitSchema },
    onSubmit: ({ value }) =>
      saveExisting({
        request: () =>
          save.mutateAsync({
            path: { brand_uuid: brand.uuid },
            body: { brandKit: formToKit(value) },
            throwOnError: true,
          }),
        invalidate: [listBrandsQueryKey(), getBrandQueryKey({ path: { brand_uuid: brand.uuid } })],
        onSaved: () => form.reset(value),
      }),
  });

  const kit = formToKit(form.state.values) as BrandKitValue;
  const hasSwatches = Object.values(kit.palette ?? {}).some((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)));

  return (
    <div className="flex flex-col gap-4">
      {hasSwatches && (
        <Card>
          <BrandKitPreview kit={kit} />
        </Card>
      )}
      <EntityFormCard label={t("kit.label")} onSubmit={() => void form.handleSubmit()}>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <form.AppField name="primary">
            {(field) => <field.TextField label={t("kit.primary")} placeholder={t("kit.colorPlaceholder")} />}
          </form.AppField>
          <form.AppField name="secondary">
            {(field) => <field.TextField label={t("kit.secondary")} placeholder={t("kit.colorPlaceholder")} />}
          </form.AppField>
          <form.AppField name="bg">
            {(field) => <field.TextField label={t("kit.background")} placeholder={t("kit.colorPlaceholder")} />}
          </form.AppField>
          <form.AppField name="text">
            {(field) => <field.TextField label={t("kit.text")} placeholder={t("kit.colorPlaceholder")} />}
          </form.AppField>
        </div>
        <form.AppField name="accents">
          {(field) => <field.TextField label={t("kit.accents")} placeholder={t("kit.accentsPlaceholder")} />}
        </form.AppField>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <form.AppField name="headingFont">
            {(field) => <field.TextField label={t("kit.headingFont")} placeholder={t("kit.headingFontPlaceholder")} />}
          </form.AppField>
          <form.AppField name="bodyFont">
            {(field) => <field.TextField label={t("kit.bodyFont")} placeholder={t("kit.bodyFontPlaceholder")} />}
          </form.AppField>
        </div>
        <form.AppField name="tone">
          {(field) => <field.TextField label={t("kit.tone")} placeholder={t("kit.tonePlaceholder")} />}
        </form.AppField>
        <form.AppForm>
          <form.SubmitActions onCancel={() => form.reset()} />
        </form.AppForm>
      </EntityFormCard>
    </div>
  );
}
